/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
// frontend/src/components/Search/SourceFilter.jsx
// 來源篩選器元件 - 左側側邊欄

import { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Collapse,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SourceIcon from '@mui/icons-material/FolderOpen';
import ClearIcon from '@mui/icons-material/Clear';
import { getActiveSources } from '../../services/sourceService';

const SourceFilter = ({ selectedSources, onSourceChange, searchFacets }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false); // 預設收合

  // searchFacets 三種 state（D4）— 同 TagFilter 語義
  const hasFacets = searchFacets !== undefined && searchFacets !== null;

  /**
   * 排序規則（D3）：已選 → facet > 0 → facet=0 墊底（按 documentCount 降序）
   * facets 缺失時跳過新排序，維持 API 原 order（documentCount 降序）
   */
  const sortedSources = useMemo(() => {
    if (!hasFacets) return sources;
    const getCount = (s) => searchFacets[s.code] ?? 0;
    const isSel = (s) => selectedSources.includes(s.code);
    return [...sources].sort((a, b) => {
      const aSel = isSel(a);
      const bSel = isSel(b);
      if (aSel !== bSel) return aSel ? -1 : 1;
      const aCnt = getCount(a);
      const bCnt = getCount(b);
      const aHas = aCnt > 0;
      const bHas = bCnt > 0;
      if (aHas !== bHas) return aHas ? -1 : 1;
      if (aHas) return bCnt - aCnt;
      return (b.documentCount || 0) - (a.documentCount || 0) || a.code.localeCompare(b.code);
    });
  }, [sources, searchFacets, selectedSources, hasFacets]);

  useEffect(() => {
    loadSources();
  }, []);

  /**
   * 載入啟用中的來源
   */
  const loadSources = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getActiveSources();
      // API 回傳格式為 { success: true, data: [...] }
      if (response.success && Array.isArray(response.data)) {
        setSources(response.data);
      } else {
        setSources([]);
        console.warn('來源資料格式異常:', response);
      }
    } catch (err) {
      console.error('載入來源失敗:', err);
      setError('無法載入來源列表');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 處理來源選擇（使用 code 作為識別）
   */
  const handleSourceToggle = (sourceCode) => {
    const newSelectedSources = selectedSources.includes(sourceCode)
      ? selectedSources.filter((code) => code !== sourceCode)
      : [...selectedSources, sourceCode];

    onSourceChange(newSelectedSources);
  };

  /**
   * 清除所有選擇
   */
  const handleClearAll = () => {
    onSourceChange([]);
  };

  /**
   * 切換展開/收合
   */
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 標題列 */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: expanded ? '1px solid #e0e0e0' : 'none',
          cursor: 'pointer',
        }}
        onClick={toggleExpanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SourceIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight="bold">
            來源篩選
            {selectedSources.length > 0 && (
              <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>
                (已選 {selectedSources.length} 個)
              </Typography>
            )}
          </Typography>
        </Box>

        <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      {/* 來源列表 */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2, maxHeight: 'calc(80vh - 80px)', overflow: 'auto' }}>
          {/* 載入中 */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          )}

          {/* 錯誤訊息 */}
          {error && !loading && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 來源清單 */}
          {!loading && !error && sources.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              尚無可用來源
            </Typography>
          )}

          {!loading && !error && sources.length > 0 && (
            <>
              {sortedSources.map((source) => {
                const isSelected = selectedSources.includes(source.code);
                const count = hasFacets
                  ? (searchFacets[source.code] ?? 0)
                  : source.documentCount || 0;
                const isDisabled = hasFacets && !isSelected && count === 0;
                return (
                  <FormControlLabel
                    key={source.code}
                    disabled={isDisabled}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSourceToggle(source.code)}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{source.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({count})
                        </Typography>
                      </Box>
                    }
                    sx={{
                      display: 'flex',
                      width: '100%',
                      ml: 0,
                      mb: 0.5,
                      opacity: isDisabled ? 0.5 : 1,
                      '&:hover': {
                        backgroundColor: isDisabled ? 'transparent' : '#f5f5f5',
                        borderRadius: 1,
                      },
                    }}
                  />
                );
              })}

              {/* 清除按鈕 */}
              {selectedSources.length > 0 && (
                <Button
                  fullWidth
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearAll}
                  sx={{ mt: 2 }}
                >
                  清除所有篩選
                </Button>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SourceFilter;
